import { OptionallyVisible } from 'components';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import merchantsApi from 'services/merchants';
import { BridgeFiatRail, MerchantInfo, PayoutSchedule as PayoutSchedulePayload } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { Select, SelectItem } from 'components/inputs/select/select';
import { Buttons } from 'components/modal/styles';
import { OffsetTime, TimePicker } from 'components/timePicker';

import { CurrencyCode } from 'types/currency';
import { ReduxToolkitError } from 'types/http';
import { DayOfMonth, FiatPayoutFrequency, Weekday } from 'types/time';

import { Delimiter, Label, Section } from '../styles';
import { PayoutType } from '../types';
import LABELS, { KEYS } from './keys';
import { ErrorText } from './styles';

interface PayoutScheduleProps {
  initialValues?: PayoutSchedulePayload;
  paymentRail?: BridgeFiatRail;
  merchant?: MerchantInfo;
  payoutType: PayoutType;
  onSuccess: () => void;
  onClose: () => void;
}

const PayoutSchedule = ({
  merchant,
  initialValues = merchant?.payoutSchedule ?? undefined,
  paymentRail,
  payoutType,
  onSuccess,
  onClose,
}: PayoutScheduleProps) => {
  const { t } = useTranslation();
  const [payoutSettingsMutation, { isLoading, error }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const initialDays = initialValues?.days?.length ? initialValues.days[0] : Weekday.Monday;
  const initialDates = initialValues?.dates?.length ? initialValues.dates[0] : DayOfMonth.FirstOfMonth;
  const initialPayoutTime: OffsetTime | undefined = initialValues?.time;
  const isMerchantPayoutCurrencyUSD = merchant?.mainFiatCurrency === CurrencyCode.USD;

  const initialFormValues = {
    [KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY]: initialValues?.frequency ?? FiatPayoutFrequency.Daily,
    [KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME]: initialPayoutTime,
    [KEYS.FORM_FIELDS.SCHEDULE.DAYS]: initialDays,
    [KEYS.FORM_FIELDS.SCHEDULE.DATES]: initialDates,
  };

  const form = useFormik({
    initialValues: initialFormValues,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!merchant) {
        throw new Error('Missing merchant to setup payout schedule with.');
      }

      const frequency = values[KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY] as FiatPayoutFrequency;
      const dayOfMonth = values[KEYS.FORM_FIELDS.SCHEDULE.DATES] as DayOfMonth;
      let payoutSchedule: PayoutSchedulePayload = {
        frequency,
        time: {
          hour: (values[KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME] as OffsetTime).hour,
          minute: (values[KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME] as OffsetTime).minute,
          period: (values[KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME] as OffsetTime).period,
          offset: (values[KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME] as OffsetTime).offset.replace('UTC', ''),
        },
      };

      if (frequency === FiatPayoutFrequency.Weekly) {
        payoutSchedule = {
          ...payoutSchedule,
          days: [values[KEYS.FORM_FIELDS.SCHEDULE.DAYS] as Weekday],
        };
      }

      if (frequency === FiatPayoutFrequency.Monthly) {
        payoutSchedule = {
          ...payoutSchedule,
          dates: [dayOfMonth === 0 ? DayOfMonth.First : dayOfMonth],
        };
      }

      const response = await payoutSettingsMutation({
        merchantId: merchant.uuid,
        payoutSchedule,
        ...(paymentRail && { paymentRail }),
      });

      if ('error' in response) {
        return;
      }

      onSuccess();
    },
  });

  const onTimeChange = (date: OffsetTime | null) => {
    if (date) {
      form.setFieldValue(KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME, date);
    } else {
      form.setFieldError(KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME, t(LABELS.INCORRECT_PAYOUT_SCHEDULE_ERROR));
    }
  };

  const hasError = (field: string) => Boolean(form.errors[field]);

  const getFrequencySelectItem = (frequency: FiatPayoutFrequency): SelectItem => ({
    key: frequency,
    label: t(LABELS.FIAT_FREQUENCY_OPTIONS[frequency]),
  });

  const availableFrequencies = isMerchantPayoutCurrencyUSD ? Object.values(FiatPayoutFrequency) : [FiatPayoutFrequency.Daily];

  const frequencyItems = availableFrequencies.map(getFrequencySelectItem);

  const getWeekdaySelectItem = (weekday: Weekday): SelectItem => ({
    key: weekday.toString(),
    label: t(LABELS.WEEKDAY_LABELS[weekday]),
  });

  const getDayOfMonthSelectItem = (day: DayOfMonth): SelectItem => ({
    key: day.toString(),
    label: t(LABELS.DAYS_OF_MONTH[day]),
  });

  const isAllowedDayForPayoutType = (weekday: Weekday, payoutType: string): boolean =>
    payoutType === PayoutType.Crypto || (weekday !== Weekday.Sunday && weekday !== Weekday.Saturday);

  const weekdayItems = Object.values(Weekday)
    .filter((weekday) => typeof weekday === 'number' && isAllowedDayForPayoutType(weekday as Weekday, payoutType))
    .map((weekday) => getWeekdaySelectItem(weekday as Weekday));

  const dayOfMonthItems = Object.values(DayOfMonth)
    .filter((day) => typeof day === 'number')
    .map((day) => getDayOfMonthSelectItem(day as DayOfMonth));

  const onFrequencyChange = (item: SelectItem) => {
    form.setFieldValue(KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY, item.key as FiatPayoutFrequency);
  };

  const onWeekdayChange = (item: SelectItem) => {
    form.setFieldValue(KEYS.FORM_FIELDS.SCHEDULE.DAYS, Number(item.key) as Weekday);
  };

  const onDayOfMonthChange = (item: SelectItem) => {
    form.setFieldValue(KEYS.FORM_FIELDS.SCHEDULE.DATES, Number(item.key) as DayOfMonth);
  };

  const weekdaySelectionAvailable = form.values[KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY] === FiatPayoutFrequency.Weekly;
  const dayOfTheMonthSelectionAvailable = form.values[KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY] === FiatPayoutFrequency.Monthly;

  return (
    <>
      <Section>
        <Label>{t(LABELS.FREQUENCY_LABEL)}</Label>
        <Select<SelectItem, false>
          fullWidth
          value={getFrequencySelectItem(form.values[KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY] as FiatPayoutFrequency)}
          items={frequencyItems}
          onChange={onFrequencyChange}
          getItemLabel={(item) => item?.label ?? item?.key ?? ''}
          multiple={false}
          error={Boolean(form.errors[KEYS.FORM_FIELDS.SCHEDULE.FREQUENCY])}
          closeDropdownOnSelect
        />
      </Section>
      <OptionallyVisible visible={weekdaySelectionAvailable}>
        <Section>
          <Label>{t(LABELS.DAY_SELECTION_LABEL)}</Label>
          <SearchableSelect<SelectItem, false>
            fullWidth
            value={getWeekdaySelectItem(form.values[KEYS.FORM_FIELDS.SCHEDULE.DAYS] as Weekday)}
            items={weekdayItems}
            onChange={onWeekdayChange}
            getItemLabel={(item) => item?.label ?? item?.key ?? ''}
            multiple={false}
            error={Boolean(form.errors[KEYS.FORM_FIELDS.SCHEDULE.DAYS])}
            closeDropdownOnSelect
            isOriginalOrder
          />
        </Section>
      </OptionallyVisible>
      <OptionallyVisible visible={dayOfTheMonthSelectionAvailable}>
        <Section>
          <Label>{t(LABELS.DAY_SELECTION_LABEL)}</Label>
          <SearchableSelect<SelectItem, false>
            fullWidth
            value={getDayOfMonthSelectItem(form.values[KEYS.FORM_FIELDS.SCHEDULE.DATES] as DayOfMonth)}
            items={dayOfMonthItems}
            onChange={onDayOfMonthChange}
            getItemLabel={(item) => item?.label ?? item?.key ?? ''}
            multiple={false}
            error={Boolean(form.errors[KEYS.FORM_FIELDS.SCHEDULE.DATES])}
            closeDropdownOnSelect
            isOriginalOrder
          />
        </Section>
      </OptionallyVisible>
      <Section>
        <Label>{t(LABELS.TIME_PICKER_LABEL)}</Label>
        <TimePicker initialValue={initialPayoutTime} onChange={onTimeChange} error={form.errors[KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME]} />
      </Section>
      <OptionallyVisible visible={Boolean(error)}>
        <ErrorText>
          {error instanceof Error ? error.message : (error as ReduxToolkitError)?.data?.errorMessage || t(LABELS.GENERIC_ERORR)}
        </ErrorText>
      </OptionallyVisible>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={onClose}>
          {t(LABELS.ACTIONS.CANCEL)}
        </Button>
        <ButtonWithLoading
          isLoading={isLoading}
          type="submit"
          disabled={hasError(KEYS.FORM_FIELDS.SCHEDULE.PAYOUT_TIME)}
          primary
          onClick={form.submitForm}
        >
          {t(LABELS.ACTIONS.SAVE)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default PayoutSchedule;
